import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, Loader2, Globe } from "lucide-react";
import { scopeChipMeta } from "../lib/scope-meta";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

interface OrgDetailResp {
  ok: boolean;
  org: {
    id: string;
    name: string;
    sub_kind: string;
    primary_scope: string | null;
    sub_scope: string | null;
    created_at: string;
    updated_at: string;
    world_knowledge: {
      extract?: string;
      summary?: string;
      wikipedia_url?: string;
      url?: string;
    } | null;
  };
  packets: Array<{ id: string; type: string; source_kind: string; text: string; captured_at: string }>;
  people: Array<{ id: string; label: string }>;
}

export function OrgDetail() {
  const { id } = useParams();
  const [data, setData] = useState<OrgDetailResp | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/orgs/${encodeURIComponent(id ?? "")}`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setData(j))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading)
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  if (!data?.org)
    return (
      <div className="max-w-3xl mx-auto px-4 py-6 text-sm text-slate-500">
        Org not found.{" "}
        <Link to="/orgs" className="text-sky-600">
          Back to orgs
        </Link>
      </div>
    );

  const o = data.org;
  const chip = scopeChipMeta(o.primary_scope, o.sub_scope);
  const wk = o.world_knowledge;
  const extract = wk?.extract || wk?.summary || "";
  const wkUrl = wk?.wikipedia_url || wk?.url || "";

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb
        segments={[
          { label: "Explore", href: "/explore" },
          { label: "Library", href: "/explore/library" },
          { label: "Orgs", href: "/orgs" },
          { label: o.name },
        ]}
      />

      <header className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-slate-500" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {o.name}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-[11px] flex-wrap">
            <span className="rounded-full px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 font-semibold">
              {o.sub_kind}
            </span>
            <span className={"rounded-full px-1.5 py-0.5 font-semibold " + chip.className}>
              {chip.label}
            </span>
          </div>
        </div>
      </header>

      <TeachingBanner id="org_detail_overview" />

      {extract && (
        <section className="glass rounded-xl border border-sky-200 dark:border-sky-800/60 bg-sky-50/50 dark:bg-sky-950/20 p-4">
          <div className="flex items-center gap-2 text-sm font-bold text-sky-800 dark:text-sky-200">
            <Globe className="w-4 h-4" /> Public info
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 leading-relaxed">
            {extract}
          </p>
          {wkUrl && (
            <a
              href={wkUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-sky-600 dark:text-sky-300 hover:underline mt-1 inline-block"
            >
              Wikipedia
            </a>
          )}
        </section>
      )}

      <section>
        <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
          Linked people
        </h2>
        {data.people.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">None yet.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.people.map((p) => (
              <span
                key={p.id}
                className="rounded-full px-2 py-1 text-xs bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
              >
                {p.label}
              </span>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
          Recent activity
        </h2>
        {data.packets.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            No recent packets mention this org.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.packets.map((p) => (
              <li
                key={p.id}
                className="glass rounded-lg border border-slate-200/70 dark:border-slate-700/50 p-3"
              >
                <div className="text-[11px] text-slate-400 dark:text-slate-500">
                  {p.source_kind} · {new Date(p.captured_at).toLocaleString()}
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200 line-clamp-2">
                  {p.text}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
